import {
  fetchDailyDailyCapacity,
  fetchDailyTimesheetForLog,
  fetchDailyTimesheetForReview,
  fetchDailyJobTimesheet,
} from "./api";
import {
  transformDailyTimesheetLog,
  transformDraftTimesheetLog,
  transformDailyTimesheetReview,
} from "./utils";
import { useQueries, useQuery } from "@tanstack/react-query";

import { TIMESHEET_QUERY_KEYS } from "./query-keys";
import { queryClient } from "utils/react-query";
import { timesheetKeys } from "./queries";
import { useMemo } from "react";
import { fetchDailyTimesheetForDraftLog } from "./api";

export async function fetchDraftTimesheetLog(context) {
  const {
    filters: { date },
  } = context;

  if (!date) {
    return {
      items: [],
      count: 0,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: () => Promise.resolve(),
    };
  }

  try {
    const timesheetData = await queryClient.fetchQuery({
      queryKey: timesheetKeys.draftLog(date),
      queryFn: () => fetchDailyTimesheetForDraftLog({ date }),
    });

    const dailyData = transformDraftTimesheetLog(
      timesheetData,
      date
    );

    const items = dailyData;
    const totalCount = dailyData?.entries?.length || 0;

    return {
      items,
      count: totalCount,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => {
        await queryClient.invalidateQueries({
          queryKey: timesheetKeys.draftLog(date),
          exact: true,
        });
        return fetchDraftTimesheetLog(context);
      },
    };
  } catch (error) {
    return {
      items: [],
      count: 0,
      error,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => fetchDraftTimesheetLog(context),
    };
  }
}

export function useTimesheetLog({ meta: { date } }) {
  const results = useQueries({
    queries: [
      {
        queryKey: timesheetKeys.log(date),
        queryFn: () => fetchDailyTimesheetForLog({ date }),
        enabled: !!date, // Only run if date is provided
      },
      {
        queryKey: timesheetKeys.acklmnt(date),
        queryFn: () => fetchDailyDailyCapacity({ date }),
        enabled: !!date, // Only run if date is provided
      },
    ],
  });

  const [timesheetQuery, capacityQuery] = results;
  const isLoading = results.some((query) => query.isLoading);
  const isFetching = results.some((query) => query.isFetching);
  const error = results.find((query) => query.error)?.error;

  const data = useMemo(() => {
    if (!timesheetQuery.data || !capacityQuery.data || !date) return null;

    const dailyData = transformDailyTimesheetLog(
      timesheetQuery.data,
      capacityQuery.data,
      date
    );

    let items = dailyData;
    let totalCount = dailyData?.entries?.length || 0;

    return {
      items,
      count: totalCount,
    };
  }, [timesheetQuery.data, capacityQuery.data, date]);

  return {
    data,
    error,
    isLoading,
    isInitialLoading: isFetching,
    refetch: async () => {
      await Promise.all(results.map((query) => query.refetch()));
    },
  };
}

export function useTimesheetReview(context) {
  const {
    filters,
    meta: { date },
  } = context;

  const {
    data: timesheetData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: timesheetKeys.review(date, filters),
    queryFn: () => fetchDailyTimesheetForReview({ filters, date }),
    enabled: !!date, // Only run if date is provided
  });

  const data = useMemo(() => {
    if (!timesheetData) return null;

    const dailyData = transformDailyTimesheetReview(timesheetData, date);

    let items = dailyData;
    let totalCount = dailyData?.entries?.length || 0;

    return {
      items,
      count: totalCount,
    };
  }, [timesheetData]);

  return {
    data,
    error,
    isLoading,
    isInitialLoading: isFetching,
    refetch,
  };
}
// Keep the old requestHandler for backward compatibility
export async function requestHandler({ filters: { date } }) {
  try {
    const timesheetData = await queryClient.fetchQuery({
      queryKey: [TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW, date],
      queryFn: () => fetchDailyTimesheetForLog({ date }),
    });

    const capacityData = await queryClient.fetchQuery({
      queryKey: [TIMESHEET_QUERY_KEYS.DAILY_CAPACITY, date],
      queryFn: () => fetchDailyDailyCapacity({ date }),
    });

    const dailyData = transformDailyTimesheetData(
      timesheetData,
      capacityData,
      date
    );

    return {
      items: [dailyData],
      count: dailyData?.entries?.length || 0,
    };
  } catch (error) {
    console.error("Error fetching data in requestHandler:", error);
    throw error;
  }
}

export async function fetchTimesheetLog(context) {
  const {
    filters: { date },
    // meta: { date },
  } = context;

  if (!date) {
    return {
      items: [],
      count: 0,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: () => Promise.resolve(),
    };
  }

  try {
    const [timesheetData, capacityData] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: timesheetKeys.log(date),
        queryFn: () => fetchDailyTimesheetForLog({ date }),
      }),
      queryClient.fetchQuery({
        queryKey: timesheetKeys.acklmnt(date),
        queryFn: () => fetchDailyDailyCapacity({ date }),
      }),
    ]);

    const dailyData = transformDailyTimesheetLog(
      timesheetData,
      capacityData,
      date
    );

    const items = dailyData;
    const totalCount = dailyData?.entries?.length || 0;

    return {
      items,
      count: totalCount,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: timesheetKeys.log(date),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: timesheetKeys.acklmnt(date),
            exact: true,
          }),
        ]);
        return fetchTimesheetLog(context);
      },
    };
  } catch (error) {
    return {
      items: [],
      count: 0,
      error,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => fetchTimesheetLog(context),
    };
  }
}

export async function fetchTimesheetForReview(context) {
  const {
    filters,
    meta: { date },
  } = context;

  if (!date) {
    return {
      items: [],
      count: 0,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: () => Promise.resolve(),
    };
  }

  try {
    const timesheetData = await queryClient.fetchQuery({
      queryKey: timesheetKeys.review(date, filters),
      queryFn: () => fetchDailyTimesheetForReview({ filters, date }),
    });

    // Transform the data
    const dailyData = transformDailyTimesheetReview(timesheetData, date);

    const items = dailyData;
    const totalCount = dailyData?.entries?.length || 0;

    return {
      items,
      count: totalCount,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => {
        await queryClient.invalidateQueries({
          queryKey: timesheetKeys.review(date, filters),
          exact: true,
        });
        return fetchTimesheetForReview(context);
      },
    };
  } catch (error) {
    return {
      items: [],
      count: 0,
      error,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => {
        return fetchTimesheetForReview(context);
      },
    };
  }
}

export async function fetchJobTimesheet(context) {
  const {
    filters,
    meta: { date },
  } = context;

  if (!date) {
    return {
      items: [],
      count: 0,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: () => Promise.resolve(),
    };
  }

  try {
    const timesheetData = await queryClient.fetchQuery({
      queryKey: timesheetKeys.review(date, filters),
      queryFn: () => fetchDailyJobTimesheet({ filters, date }),
    });

    // Transform the data
    const dailyData = transformDailyTimesheetReview(timesheetData, date);

    const items = dailyData;
    const totalCount = dailyData?.entries?.length || 0;

    return {
      items,
      count: totalCount,
      error: null,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => {
        await queryClient.invalidateQueries({
          queryKey: timesheetKeys.review(date, filters),
          exact: true,
        });
        return fetchTimesheetForReview(context);
      },
    };
  } catch (error) {
    return {
      items: [],
      count: 0,
      error,
      isLoading: false,
      isInitialLoading: false,
      refetch: async () => {
        return fetchTimesheetForReview(context);
      },
    };
  }
}
